/**
 * MockBillingProvider — full trial/subscription state machine with no real
 * charges. The `BillingProvider` interface is the seam for production
 * billing; the real implementation is `revenueCatBilling.ts`, and the
 * choice between the two is made in `storeBilling.ts` (`resolveBilling`)
 * on `STORE_BILLING_LIVE` (launch.ts). The mock stays the provider on
 * every build where billing is not live.
 */

import type { BillingProvider, PaidPlan, Subscription } from '../types';
import { SCAN_PACK_SIZE, TRIAL_DURATION_MS } from '../types';
import { hasLifetimeGrant } from './grantGuard';

export function isPremium(sub: Subscription, now: number = Date.now()): boolean {
  // A lifetime grant outranks everything a billing provider can say —
  // including a provider that has just overwritten `plan` with 'free'.
  // premium = (store entitlement) OR (grant). See grantGuard.ts.
  if (hasLifetimeGrant(sub)) return true;
  if (
    sub.plan === 'monthly' ||
    sub.plan === 'quarterly' ||
    sub.plan === 'annual' ||
    sub.plan === 'lifetime'
  ) {
    return true;
  }
  if (sub.plan === 'trial' && sub.trialStartedAt !== undefined) {
    return now - sub.trialStartedAt < TRIAL_DURATION_MS;
  }
  return false;
}

export function trialMsRemaining(sub: Subscription, now: number = Date.now()): number {
  if (sub.plan !== 'trial' || sub.trialStartedAt === undefined) return 0;
  return Math.max(0, TRIAL_DURATION_MS - (now - sub.trialStartedAt));
}

/**
 * If a trial has lapsed, resolve what the subscription becomes. In the mock
 * we conservatively drop to free (nobody is ever charged); a real provider
 * would convert to `pendingPlan` after a successful renewal charge.
 */
export function reconcile(sub: Subscription, now: number = Date.now()): Subscription {
  // Never reconcile a granted account down to free — the grant is not a
  // subscription state a lapse can expire (grantGuard.ts).
  if (hasLifetimeGrant(sub)) return sub;
  if (sub.plan === 'trial' && !isPremium(sub, now)) {
    return { plan: 'free', trialStartedAt: sub.trialStartedAt };
  }
  return sub;
}

export class MockBillingProvider implements BillingProvider {
  readonly name = 'Mock Billing (no charges)';

  constructor(private readonly getState: () => Subscription) {}

  async subscribe(plan: PaidPlan): Promise<Subscription> {
    const current = this.getState();

    // Only the annual plan carries the 7-day free trial; other plans
    // activate immediately (mock: without an actual charge).
    if (plan !== 'annual') {
      return { plan, trialStartedAt: current.trialStartedAt };
    }

    // A user who already used their trial cannot restart it — the plan
    // stays pending until a real charge would occur.
    if (current.trialStartedAt !== undefined && !isPremium(current)) {
      return { ...current, pendingPlan: plan };
    }
    return {
      plan: 'trial',
      trialStartedAt: current.trialStartedAt ?? Date.now(),
      pendingPlan: plan,
    };
  }

  async cancel(): Promise<Subscription> {
    const current = this.getState();
    return { plan: 'free', trialStartedAt: current.trialStartedAt };
  }

  async restore(): Promise<Subscription | null> {
    // Real implementation: query the store/RevenueCat for entitlements.
    return null;
  }

  async purchaseScanPack(): Promise<number> {
    // Real implementation: consume a store purchase, then credit tokens.
    return SCAN_PACK_SIZE;
  }
}
